/**
 * Pure helpers for admin Assessment Details status / peer-reviewer wiring (14744).
 */

import { ASSESSMENT_STATUS_DROPDOWN } from "./assessmentStatuses";

const EXPLICIT_STATUS_PASSTHROUGH = new Set([
  "Assessment Complete",
  "Assessment in Progress",
  "Report Published to Library",
  "Assessment Closed",
]);

/**
 * Map dropdown selection → vqa_status sent on Save (null = let BE derive).
 * @param {string|null|undefined} selectedStatus
 * @returns {string|null}
 */
export function resolveExplicitAdminVqaStatus(selectedStatus) {
  if (!selectedStatus) return null;
  if (selectedStatus === "Published to Library") {
    return "Report Published to Library";
  }
  if (EXPLICIT_STATUS_PASSTHROUGH.has(selectedStatus)) {
    return selectedStatus;
  }
  return null;
}

/**
 * @param {{ vqa_auditor_id?: string|null, vqa_auditor_name?: string|null }|null|undefined} peer
 * @returns {{ id: string|null, name: string|null }|null}
 */
export function peerReviewerFormValueFromApi(peer) {
  if (!peer?.vqa_auditor_name) return null;
  return {
    id: peer.vqa_auditor_id ?? null,
    name: peer.vqa_auditor_name,
  };
}

/**
 * @param {{ id?: string|null, name?: string|null }|null|undefined} formValue
 * @returns {{ peerReviewerId: string|null, peerReviewerName: string|null }}
 */
export function peerReviewerPayloadFromForm(formValue) {
  return {
    peerReviewerId: formValue?.id ?? null,
    peerReviewerName: formValue?.name ?? null,
  };
}

/**
 * @param {string|null|undefined} status
 * @returns {boolean}
 */
export function shouldClearAdminSelectedStatus(status) {
  return (
    status === "Draft Review by Client" ||
    status === "In Sponsor Review" ||
    status === "Draft Report Delivered"
  );
}

export const AUDITOR_REQUIRES_SCOPE_MEETING_MESSAGE =
  "Please select a Scope Planning Meeting date before assigning an Auditor.";

/**
 * Confirmation modal kind for CheckedDropdown status picks.
 * @param {string|null|undefined} title
 * @returns {'approve'|'inProgress'|'complete'|'publish'|'close'|'decline'|'cancel'|'direct'|null}
 */
export function getAssessmentStatusConfirmKind(title) {
  if (!title) return null;
  if (title === "Request Accepted") return "approve";
  if (title === "Assessment in Progress") return "inProgress";
  if (title === "Assessment Complete") return "complete";
  if (
    title === "Report Published to Library" ||
    title === "Published to Library"
  ) {
    return "publish";
  }
  if (title === "Assessment Closed") return "close";
  if (title === "Request Declined") return "decline";
  if (title === "Canceled Assessment") return "cancel";
  return "direct";
}

export function isPublishedLibraryStatus(status) {
  return (
    status === "Report Published to Library" ||
    status === "Published to Library"
  );
}

const CLOSE_ALLOWED_STATUS_ALIASES = new Set([
  "Final Report Delivered to Sponsor",
  "Report Anonymized",
  "Report Published to Library",
]);

/**
 * Assessment Closed is a lifecycle exit, not the next sequential step.
 * It stays available once the assessment has reached Final Report Delivered.
 * @param {string|null|undefined} status
 * @param {Array<{ title: string }>} [options]
 * @returns {boolean}
 */
export function canCloseAssessmentFromStatus(
  status,
  options = ASSESSMENT_STATUS_DROPDOWN
) {
  if (status == null || status === "") return false;
  if (
    status === "Assessment Closed" ||
    status === "Canceled Assessment" ||
    status === "Cancelled"
  ) {
    return false;
  }
  if (CLOSE_ALLOWED_STATUS_ALIASES.has(status)) return true;

  const floor = options.findIndex(
    (option) => option.title === "Final Report Delivered"
  );
  const current = options.findIndex((option) => option.title === status);
  if (floor < 0 || current < 0) return false;
  return current >= floor;
}
